import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Divider from "veneer/Divider";
import Drawer from "veneer/Drawer";
import FilePicker from "veneer/FilePicker";
import { CloseIconButton } from "veneer/IconButton";
import ProgressBar from "veneer/ProgressBar";
import { showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import {
  ErrorStopSignOutlinedIcon,
  LibraryAddCheckIcon,
  UploadFileOutlinedIcon,
} from "veneer/SvgIcon";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { CsvService, flatten } from "cortex/ui/src/service/csv";
import { deviceService } from "cortex/ui/src/service/device";
import { formatFileSize } from "cortex/ui/src/ui/table/formats";
import { Option } from "cortex/ui/src/ui/table/types";
import { socket } from "cortex/ui/src/websocket";
import sampleRegistrationCsv from "cortex/ui/static/files/cortex_bulk_registration_sample.csv";
import sampleRegistrationNoInstallCsv from "cortex/ui/static/files/cortex_bulk_registration_sample_not_installed.csv";

import BaseDrawer, { ErrorListItem, ListItem, ListWrapper } from "./BaseDrawer";

export interface RegisterBulkDevicesDrawerProps {
  networkOptions: ReadonlyArray<Option>;
  onCancel: () => void;
  open: boolean;
}

interface SelectedFileInfoProps {
  file: File;
  onClear: () => void;
  processing?: boolean;
  totalNewDevices?: number;
  validCsv: boolean | null;
}

type FormValues = {
  csvFile: File | null;
};

type CsvRow = {
  [key: string]: string;
};

interface BulkListItemRow {
  device: CsvRow;
  isValid: boolean;
  reason?: string;
}

const csvService = new CsvService();
const DEFAULT_FORM: FormValues = { csvFile: null };

const REQUIRED_ALWAYS = ["device_name", "network_name"] as const;
const REQUIRED_EITHER = ["device_uid", "device_shell_uid"] as const;

const normalize = (s: string) => s.trim().toLowerCase();

function isCsvFile(file: File) {
  const nameOk = file.name.toLowerCase().endsWith(".csv");
  const type = (file.type || "").toLowerCase();
  const typeOk = type.includes("csv");
  return nameOk || typeOk;
}

function missingRequiredHeaders(headers: string[]) {
  const set = new Set(headers.map(normalize));
  const missingAlways = REQUIRED_ALWAYS.filter((h) => !set.has(h));
  const hasEither = REQUIRED_EITHER.some((h) => set.has(h));
  const missingEither = hasEither ? [] : [...REQUIRED_EITHER];
  return [...missingAlways, ...missingEither];
}

function getValidCsvRow(
  device: Record<string, string>,
  networkOptions: ReadonlyArray<Option>,
  setValidCsv: (isValid: boolean) => void,
): BulkListItemRow {
  const networkName = device["network_name"]?.trim() || "";
  const networkExists = networkOptions.some((opt) => opt.label === networkName);

  if (!networkExists) {
    setValidCsv(false);
    return {
      device,
      isValid: false,
      reason: `Network does not exist`,
    };
  }

  return {
    device,
    isValid: true,
  };
}

export default function RegisterBulkDevicesDrawer({
  networkOptions,
  onCancel,
  open,
}: RegisterBulkDevicesDrawerProps) {
  const [csvRows, setCsvRows] = useState<ReadonlyArray<BulkListItemRow>>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormValues>(DEFAULT_FORM);
  const [processing, setProcessing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validCsv, setValidCsv] = useState<boolean | null>(null);

  const clearFile = useCallback(() => {
    setForm(DEFAULT_FORM);
    setCsvRows([]);
    setError(null);
    setProcessing(false);
    setSubmitted(false);
    setValidCsv(null);
  }, []);

  useEffect(() => {
    if (!open) clearFile();
  }, [open, clearFile]);

  const processCsv = useCallback(
    (file: File) => {
      const reader = new FileReader();

      const failCsv = (reason: string) => {
        setError(reason);
        setValidCsv(false);
        setCsvRows([]);
        setProcessing(false);
      };

      setError(null);
      setValidCsv(null);
      setCsvRows([]);
      setProcessing(true);

      if (!isCsvFile(file)) {
        failCsv("Invalid file type. Please upload a CSV file (.csv).");
        return;
      }

      reader.onload = () => {
        try {
          const text = reader.result as string;

          const decoded =
            csvService.decode<Record<string, string | null>>(text) ?? [];

          if (!decoded.length) {
            failCsv(
              "Invalid CSV: no rows were found. Ensure the file contains data.",
            );
            return;
          }

          const headers = Object.keys(decoded[0] ?? {});
          if (!headers.length) {
            failCsv(
              "Invalid CSV: no headers were found. Ensure the first row contains header names like device_name, network_name, and device_uid or device_shell_uid.",
            );
            return;
          }

          const missing = missingRequiredHeaders(headers);
          if (missing.length) {
            failCsv("Invalid CSV: missing required header(s)");
            return;
          }

          const rows: Record<string, string>[] = decoded.map((row) => {
            const flatRow = flatten(row);
            return Object.fromEntries(
              Object.entries(flatRow).map(([k, v]) => [
                k,
                (v ?? "").toString(),
              ]),
            );
          });

          const validatedRows = rows.map((device) =>
            getValidCsvRow(device, networkOptions, setValidCsv),
          );

          setCsvRows(validatedRows);

          const allValid = validatedRows.every((r) => r.isValid);
          setValidCsv(allValid);
        } catch (err) {
          failCsv(
            "An unexpected error occurred while processing the CSV file. Please try again.",
          );
        } finally {
          setProcessing(false);
        }
      };

      reader.onerror = () => failCsv("Failed to read the CSV file.");
      reader.onabort = () => failCsv("CSV read was aborted.");

      reader.readAsText(file);
    },
    [networkOptions],
  );

  const handleRegisterDevices = useCallback(async () => {
    setError(null);

    if (!form.csvFile) {
      setError("No CSV file selected.");
      return;
    }

    if (csvRows.length === 0) {
      setError("The CSV contains no devices to register.");
      return;
    }

    const totalInvalidDevices = csvRows.filter((r) => !r.isValid).length;
    if (totalInvalidDevices > 0) {
      setError(
        `The CSV contains ${totalInvalidDevices} invalid device(s). Please correct the errors and try again.`,
      );
      return;
    }

    if (validCsv !== true) {
      setError("Please select a valid CSV file to register devices in bulk.");
      return;
    }

    setSubmitted(true);
    try {
      const data = await deviceService
        .bulkRegister(form.csvFile, socket.id)
        .then((resp: any) => resp.data.data);

      showSuccess(
        typeof data?.created === "number"
          ? `Registered ${data.created} device(s).`
          : "Bulk registration started.",
      );

      onCancel();
    } catch (_err: any) {
      setError(
        _err?.message ||
          "An unexpected error occurred while registering the device. Please try again.",
      );
    } finally {
      setSubmitted(false);
    }
  }, [csvRows.length, form.csvFile, onCancel, validCsv]);

  const drawerHeader = (
    <>
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        width="100%"
      >
        {error && <Alert severity="error">{error}</Alert>}
        <Typography>
          Download the template, populate the file, then upload.
        </Typography>
        <FilePicker
          accept={[".csv", "text/csv"]}
          onChange={(file: File | null) => {
            setForm({ csvFile: file });
            setValidCsv(null);
            setError(null);
            setCsvRows([]);
            if (file) processCsv(file);
          }}
          variant="contained"
        >
          Upload CSV
        </FilePicker>
        <Stack alignItems="center" justifyContent="center" spacing={0.5}>
          <Typography>
            <Link download target="_blank" to={sampleRegistrationCsv}>
              Download this template
            </Link>{" "}
            if Cortex <b>is installed</b> on your players
          </Typography>
          <Typography>
            <Link download target="_blank" to={sampleRegistrationNoInstallCsv}>
              Download this template
            </Link>{" "}
            if Cortex is <b>not yet</b> installed
          </Typography>
        </Stack>
        <Typography>
          <a
            href="https://help.vistarmedia.com/hc/en-us/articles/44591725306381-Devices-Management#Bulk"
            target="_blank"
          >
            Learn more
          </a>{" "}
          about bulk registration
        </Typography>
      </Stack>
      {form.csvFile && (
        <SelectedFileInfo
          file={form.csvFile}
          onClear={clearFile}
          processing={processing}
          totalNewDevices={csvRows.length}
          validCsv={validCsv}
        />
      )}
    </>
  );

  return (
    <Drawer
      commitLabel="Register"
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleRegisterDevices}
      open={open}
      padding={0}
      title="Register Devices"
    >
      <BaseDrawer drawerHeader={drawerHeader}>
        {csvRows.map(({ device, isValid, reason }, index) => (
          <ListWrapper disablePadding key={index}>
            <BulkListItem device={device} isValid={isValid} reason={reason} />
          </ListWrapper>
        ))}
      </BaseDrawer>
    </Drawer>
  );
}

const SelectedFileInfo = ({
  file,
  onClear,
  processing,
  totalNewDevices = 0,
  validCsv,
}: SelectedFileInfoProps) => {
  const { name, size } = file;

  const status = useMemo(() => {
    if (processing) {
      return {
        bar: undefined as "success" | "error" | undefined,
        color: Colors.gray1000,
        text: "Uploading",
      };
    }
    if (validCsv === true) {
      return {
        bar: "success" as const,
        color: Colors.green700,
        text: `Complete: Found ${totalNewDevices} device(s)`,
      };
    }
    if (validCsv === false) {
      return {
        bar: "error" as const,
        color: Colors.red700,
        text: "Failed: Invalid CSV file",
      };
    }
    return {
      bar: undefined as "success" | "error" | undefined,
      color: Colors.gray1000,
      text: `Found ${totalNewDevices} device(s)`,
    };
  }, [processing, totalNewDevices, validCsv]);

  return (
    <>
      <Divider />
      <Stack direction="row" mb={4} spacing={2}>
        <Box color={status.color}>
          <UploadFileOutlinedIcon color="inherit" />
        </Box>
        <Stack
          alignItems="center"
          direction="row"
          flex={1}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack flex={1} spacing={0.5}>
            <Typography color={status.color}>{name}</Typography>
            <Typography variant="caption">
              {formatFileSize(size)} • {status.text}
            </Typography>
            {!processing && status.bar && (
              <ProgressBar
                percent={100}
                tooltip={status.text}
                type={status.bar}
              />
            )}
          </Stack>
          <CloseIconButton color="inherit" onClick={onClear} />
        </Stack>
      </Stack>
    </>
  );
};

const BulkListItem = ({ device, isValid, reason }: BulkListItemRow) => {
  const ListComponent = isValid ? ListItem : ErrorListItem;

  return (
    <ListComponent>
      <Stack alignItems="center" direction="row" spacing={2}>
        {isValid ? (
          <LibraryAddCheckIcon />
        ) : (
          <ErrorStopSignOutlinedIcon color="error" />
        )}
        <Stack flex={1}>
          <Typography color={Colors.gray1000} variant="body1">
            {device?.device_name || "Unknown Device"}
          </Typography>
          <Typography variant="body2">
            {device?.network_name || "N/A"}
            {!isValid && reason && ` - ${reason}`}
          </Typography>
        </Stack>
      </Stack>
    </ListComponent>
  );
};
